import { CloseIcon } from "components/ui/icons/uploadbar/CloseIcon"
import { ChevronLeftIcon } from "./icons/ChevronLeftIcon"
import { ChevronRightIcon } from "./icons/ChevronRightIcon"
import { ReturnIcon } from "./icons/ReturnIcon"
import style from "./JumpingNavigationBar.module.scss"

const {
	bar,
	barCompact,
	left,
	center,
	right,
	button,
	title,
	counter,
	backButton,
	backIcon,
	closeButton,
	iconButton,
} = style

type Props = {
	title: string
	currentIndex: number
	total: number
	/**
	 * Greys out back / prev / next while another mode owns the document area (the connect-objects
	 * picker, REQ-372) — stepping the session would scroll away from the objects being picked and
	 * take over their selection. Close stays live so the bar can be dismissed if it's in the way.
	 */
	isNavigationDisabled?: boolean
	/**
	 * Drops the source title and tightens the padding, for the bar rendered inside ONE pane of the
	 * split screen (#1311). At the design's ~358px pane width the label has nowhere to go, so the
	 * back button is icon-only there — the counter and the prev/next buttons are what has to fit.
	 */
	isCompact?: boolean
	/**
	 * Noun for what prev/next step through, used in their accessible labels. Defaults to "callout":
	 * every session was over elevation callouts until #1311 made ecall→eref sessions a core path,
	 * and an eref session labelled "Previous callout" names the wrong thing.
	 */
	itemNoun?: string
	onBack: () => void
	onPrev: () => void
	onNext: () => void
	onClose: () => void
}

export const JumpingNavigationBar = ({
	title: titleText,
	currentIndex,
	total,
	isNavigationDisabled = false,
	isCompact = false,
	itemNoun = "callout",
	onBack,
	onPrev,
	onNext,
	onClose,
}: Props) => {
	const prevDisabled = isNavigationDisabled || currentIndex <= 0
	const nextDisabled = isNavigationDisabled || currentIndex >= total - 1

	return (
		<div className={`${bar}${isCompact ? ` ${barCompact}` : ""}`} role="toolbar" aria-label="Jumping navigation">
			<div className={left}>
				<button
					type="button"
					className={`${button} ${backButton}`}
					onClick={onBack}
					disabled={isNavigationDisabled}
					aria-label={isCompact ? `Back to ${titleText}` : "Back to object"}
					title={isCompact ? titleText : undefined}
				>
					<span className={backIcon} aria-hidden="true">
						<ReturnIcon />
					</span>
					{isCompact ? null : <span className={title}>{titleText}</span>}
				</button>
			</div>
			<div className={center}>
				<button
					type="button"
					className={`${button} ${iconButton}`}
					onClick={onPrev}
					disabled={prevDisabled}
					aria-label={`Previous ${itemNoun}`}
				>
					<ChevronLeftIcon />
				</button>
				<span className={counter}>
					{currentIndex + 1} / {total}
				</span>
				<button
					type="button"
					className={`${button} ${iconButton}`}
					onClick={onNext}
					disabled={nextDisabled}
					aria-label={`Next ${itemNoun}`}
				>
					<ChevronRightIcon />
				</button>
			</div>
			<div className={right}>
				<button type="button" className={`${button} ${closeButton}`} onClick={onClose} aria-label="Close jumping bar">
					<CloseIcon />
				</button>
			</div>
		</div>
	)
}
